// Single source of truth for the Group admin (Circle / Network / Space)
// section enum + per-kind allowed-section lists.
//
// Consumed by:
//   - `GroupAdminShell.tsx` (renders the sidebar from these lists)
//   - `lib/route-factories/group-admin-route.ts` (validates `?section=…`)
//
// Adding a new section means adding it here, in `GroupAdminShell`'s
// `SIDEBAR_ITEM_DEFS`, and in the section-dispatch table at the route
// call site — three touchpoints, one keyed enum.

export type GroupAdminKind = 'circle' | 'network' | 'space';

export type GroupAdminSection =
  | 'overview'
  | 'visibility'
  | 'join-questions'
  | 'requests'
  | 'members'
  | 'circles'
  | 'broadcasts'
  | 'archive';

// Per-kind allowed sections (includes 'overview'). The sidebar in
// `GroupAdminShell` derives its items from `SECTIONS_BY_KIND[kind]` minus
// 'overview' (which is the implicit default landing item).
export const SECTIONS_BY_KIND: Record<
  GroupAdminKind,
  ReadonlyArray<GroupAdminSection>
> = {
  circle: ['overview', 'visibility', 'join-questions', 'requests', 'members', 'broadcasts', 'archive'],
  network: ['overview', 'visibility', 'join-questions', 'requests', 'members', 'circles', 'broadcasts', 'archive'],
  space: ['overview', 'visibility', 'members', 'broadcasts', 'archive'],
};

export function resolveGroupAdminSection(
  kind: GroupAdminKind,
  raw: unknown,
): GroupAdminSection {
  const allowed = SECTIONS_BY_KIND[kind] as ReadonlyArray<string>;
  if (typeof raw === 'string' && allowed.includes(raw)) {
    return raw as GroupAdminSection;
  }
  return 'overview';
}
